import { useState, useCallback } from 'react';
import { locationApi } from '../api/location';

/**
 * Hook for handling location tracking
 * @returns {Object} - Location tracking methods and state
 */
export const useLocation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Get the current device information
   * @returns {Object} - Device information
   */
  const getDeviceInfo = useCallback(() => {
    return {
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      platform: navigator.platform,
      language: navigator.language,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }, []);

  /**
   * Get the current location
   * @returns {Promise<{latitude: number, longitude: number}>} - Location coordinates
   */
  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          reject(error);
        },
        { timeout: 10000, enableHighAccuracy: true }
      );
    });
  }, []);

  /**
   * Log a user action with location data
   * @param {string} actionType - The type of action (login, bet, withdrawal, etc.)
   * @param {Object} additionalData - Additional data to include in the request
   * @returns {Promise} - The API response
   */
  const logAction = useCallback(async (actionType, additionalData = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      // Get current location
      let locationData = { latitude: null, longitude: null };
      try {
        locationData = await getCurrentLocation();
      } catch (locationError) {
        console.warn('Could not get location:', locationError);
        // Continue with null coordinates
      }

      // Get device info
      const deviceInfo = getDeviceInfo();

      // Prepare payload
      const payload = {
        ...locationData,
        type: actionType,
        deviceInfo: {
          ...deviceInfo,
          ...additionalData,
        },
      };

      // Send to API
      const response = await locationApi.logAction(payload);
      setIsLoading(false);
      return response;
    } catch (error) {
      console.error('Error logging action:', error);
      setError(error);
      setIsLoading(false);
      throw error;
    }
  }, [getCurrentLocation, getDeviceInfo]);

  return {
    logAction,
    getCurrentLocation,
    getDeviceInfo,
    isLoading,
    error,
  };
}; 